import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Grid,
    Card,
    CardContent,
    CardMedia,
    Button,
    Stack,
    styled,
    useTheme,
    useMediaQuery,
    TextField,
    InputAdornment,
    Fab,
    Zoom,
    LinearProgress,
} from "@mui/material";

/* Icons */
import PersonalVideoIcon from "@mui/icons-material/PersonalVideo";
import CodeIcon from "@mui/icons-material/Code";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

/* Custom Components */
import RadioOption from "../../../components/RadioOption";
import Typography from "../../../components/Typography";
import LectureTimer from "../../../components/LectureTimer";
import LanguageTabs from "../../../components/LanguageTabs";

/* Assets */
const bgImage =
    "https://images.unsplash.com/photo-1518770660439-4636190af475";

/* Styled Components */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    color: "white",
    paddingTop: theme.spacing(15),
    paddingBottom: theme.spacing(10),
}));

const DashboardCard = styled(Card)(() => ({
    backgroundColor: "rgba(255,255,255,0.95)",
    borderRadius: "12px",
    overflow: "hidden",
}));

const TopicBox = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    backgroundColor: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "10px",
    padding: theme.spacing(1.5, 2),
    transition: "0.3s",
    "&:hover": {
        backgroundColor: "rgba(218, 165, 32, 0.1)",
        borderColor: "goldenrod",
    },
}));

const ViewButton = styled(Button)(() => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 700,
    borderRadius: "999px",
    padding: "6px 20px",
    textTransform: "none",
}));

/* ---------------- MYSQL TOPICS ---------------- */
const mysqlTopics = [
    { label: "Learning Journey & Database Concepts", to: "/mysql-learning-journey" },
    { label: "Installing & Configuring MySQL", to: "/mysql-installation" },
    { label: "Data Types & Table Design", to: "/mysql-datatypes-tables" },
    { label: "CRUD Operations (SELECT, INSERT, UPDATE, DELETE)", to: "/mysql-crud" },
    { label: "Filtering, Sorting & Functions", to: "/mysql-filtering-functions" },
    { label: "Joins & Relationships", to: "/mysql-joins" },
    { label: "Subqueries & Views", to: "/mysql-subqueries-views" },
    { label: "Indexes & Query Optimization", to: "/mysql-indexing-performance" },
    { label: "Transactions & ACID Properties", to: "/mysql-transactions" },
    { label: "Stored Procedures & Triggers", to: "/mysql-stored-programs" },
    { label: "Window Functions & Analytics", to: "/mysql-window-functions" },
    { label: "Security & User Management", to: "/mysql-security" },
    { label: "Backup, Restore & Recovery", to: "/mysql-backup-recovery" },
    { label: "MySQL with Applications (JPA / Node)", to: "/mysql-integration" },
    { label: "MySQL Assignments", to: "/mysql-assignments" },
    { label: "MySQL Assignment Solutions", to: "/mysql-assignment-solutions" },
    { label: "MySQL Quiz", to: "/mysql-quiz" },
    { label: "MySQL Mini Project", to: "/mysql-project-practical" },
    { label: "Certification Exam", to: "/mysql-examination" },
];

/* ---------------- MAIN COMPONENT ---------------- */
export default function MySQLDashboard() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [searchQuery, setSearchQuery] = useState("");
    const [showScroll, setShowScroll] = useState(false);
    const [completedCount] = useState(0);

    const filteredTopics = useMemo(
        () =>
            mysqlTopics.filter(topic =>
                topic.label.toLowerCase().includes(searchQuery.toLowerCase())
            ),
        [searchQuery]
    );

    const progressPercentage =
        (completedCount / mysqlTopics.length) * 100;

    React.useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    return (
        <PageWrapper>
            {/* Banner */}
            <Box sx={{ textAlign: "center", mb: 4, mt: 16 }}>
                <Typography
                    variant={isMobile ? "h6" : "h3"}
                    sx={{
                        fontWeight: 700,
                        color: "goldenrod",
                        px: 6,
                        py: 1,
                        borderRadius: "999px",
                        display: "inline-block",
                    }}
                >
                    MYSQL DATABASE
                </Typography>
            </Box>

            <LectureTimer />
            <LanguageTabs active="MySQL" />

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* Video Panel */}
                <Grid item xs={12} md={5}>
                    <DashboardCard>
                        <CardMedia
                            component="iframe"
                            height={isMobile ? "200" : "280"}
                            src="https://www.youtube.com/embed/7S_tz1z_5bA"
                            title="MySQL Full Course"
                            allowFullScreen
                        />
                        <CardContent sx={{ textAlign: "center" }}>
                            <Typography fontWeight={800}>
                                Mastering MySQL Database
                            </Typography>
                            <Stack direction="row" justifyContent="center" spacing={1}>
                                <PersonalVideoIcon />
                                <Typography variant="body2">
                                    Self‑paced Learning
                                </Typography>
                            </Stack>
                            <Link to="/sql-editor">
                                <Button
                                    fullWidth
                                    startIcon={<CodeIcon />}
                                    sx={{ mt: 2, bgcolor: "#100720", color: "white" }}
                                >
                                    Open SQL Playground
                                </Button>
                            </Link>
                        </CardContent>
                    </DashboardCard>
                </Grid>

                {/* Curriculum Panel */}
                <Grid item xs={12} md={7}>
                    <Typography textAlign="center" fontWeight={700}>
                        MYSQL MASTERY CURRICULUM
                    </Typography>

                    <LinearProgress
                        variant="determinate"
                        value={progressPercentage}
                        sx={{
                            my: 2,
                            height: 8,
                            "& .MuiLinearProgress-bar": {
                                bgcolor: "goldenrod",
                            },
                        }}
                    />

                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search MySQL topic..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: "goldenrod" }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ mb: 3 }}
                    />

                    <Stack spacing={1.5}>
                        {filteredTopics.map(topic => (
                            <TopicBox key={topic.to}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <RadioOption />
                                    <Typography>{topic.label}</Typography>
                                </Stack>
                                <Link to={topic.to}>
                                    <ViewButton size="small">View</ViewButton>
                                </Link>
                            </TopicBox>
                        ))}
                    </Stack>
                </Grid>
            </Grid>

            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={() =>
                        window.scrollTo({ top: 0, behavior: "smooth" })
                    }
                    sx={{
                        position: "fixed",
                        bottom: 25,
                        right: 25,
                        bgcolor: "goldenrod",
                    }}
                >
                    <KeyboardArrowUpIcon />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
